import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import type { Document, DocumentVersion } from "@/types/document";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { prepareDocumentForPreview } from "@/utils/documentFormatter";

export function useDocumentOperations() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [previewDocument, setPreviewDocument] = useState<Document | null>(null);
  const [previewContent, setPreviewContent] = useState<string>("");
  const [downloadDocument, setDownloadDocument] = useState<Document | null>(null);
  const [editingDocument, setEditingDocument] = useState<Document | null>(null);

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      console.log('Deleting document with ID:', id);
      
      const { data: document } = await (supabase as any)
        .from('documents')
        .select('file_path')
        .eq('id', id)
        .single();

      if (document?.file_path) {
        console.log('Deleting file from storage:', document.file_path);
        const { error: storageError } = await supabase.storage
          .from('documents')
          .remove([document.file_path]);

        if (storageError) {
          console.error('Error deleting file from storage:', storageError);
          throw storageError;
        }
      }

      const { error } = await (supabase as any)
        .from('documents')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting document from database:', error);
        throw error;
      }
      
      console.log('Document deleted successfully');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast({
        title: "Success",
        description: "Document deleted successfully",
      });
    },
    onError: (error) => {
      console.error('Error in delete mutation:', error);
      toast({
        title: "Error",
        description: "Failed to delete document",
        variant: "destructive",
      });
    },
  });

  const updateDocumentMutation = useMutation({
    mutationFn: async ({ 
      documentId, 
      placeholderValues, 
      content,
      filename 
    }: { 
      documentId: string; 
      placeholderValues: Record<string, string>;
      content: string;
      filename?: string;
    }) => {
      console.log('Updating document:', documentId);
      
      try {
        // Get the current document to determine next version number
        const { data: currentDocument, error: fetchError } = await (supabase as any)
          .from('documents')
          .select('*')
          .eq('id', documentId)
          .single();
          
        if (fetchError) throw fetchError;
        
        // Get the user information
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');
        
        // Check if a version with this document_id and version_number already exists
        const { data: existingVersions, error: versionCheckError } = await (supabase as any)
          .from('document_versions')
          .select('version_number')
          .eq('document_id', documentId)
          .eq('version_number', currentDocument.version_number);
        
        if (versionCheckError) throw versionCheckError;
        
        // Only create a version record if it doesn't exist already
        if (existingVersions.length === 0) {
          console.log('Creating new version record for document:', documentId);
          // Create a new version record of the current document state before updating
          const { error: versionError } = await (supabase as any)
            .from('document_versions')
            .insert({
              document_id: documentId,
              version_number: currentDocument.version_number,
              content: currentDocument.content,
              placeholder_values: currentDocument.placeholder_values,
              created_by: user.id
            });
            
          if (versionError) {
            console.error('Error creating version record:', versionError);
            throw versionError;
          }
        } else {
          console.log('Version record already exists, skipping creation');
        }

        // Set current timestamp for updated_at
        const now = new Date().toISOString();

        // Update the document with new content and increment version number
        const updateData: any = {
          content,
          placeholder_values: placeholderValues,
          version_number: currentDocument.version_number + 1,
          updated_at: now
        };
        
        // Only update filename if provided
        if (filename) {
          updateData.filename = filename;
        }
        
        console.log('Updating document with data:', updateData);
        
        const { error: updateError } = await (supabase as any)
          .from('documents')
          .update(updateData)
          .eq('id', documentId);
          
        if (updateError) {
          console.error('Error updating document:', updateError);
          throw updateError;
        }
        
        // Immediately invalidate the document cache to ensure fresh data
        queryClient.invalidateQueries({ queryKey: ['document', documentId] });
        queryClient.invalidateQueries({ queryKey: ['document-versions', documentId] });
        
        return { success: true };
      } catch (error) {
        console.error('Error updating document:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast({
        title: "Success",
        description: "Document updated successfully",
      });
    },
    onError: (error) => {
      console.error('Error in update mutation:', error);
      toast({
        title: "Error",
        description: "Failed to update document",
        variant: "destructive",
      });
    },
  });

  const handlePreview = async (document: Document) => {
    try {
      console.log('Previewing document:', document);
      if (!document.file_path) {
        console.error('No file path found for document:', document);
        toast({
          title: "Error",
          description: "Document file path not found",
          variant: "destructive",
        });
        return;
      }

      console.log('Downloading document from storage:', document.file_path);
      const { data, error } = await supabase.storage
        .from('documents')
        .download(document.file_path);

      if (error) {
        console.error('Error downloading document for preview:', error);
        throw error;
      }

      const rawContent = await data.text();
      console.log('Document content loaded successfully');
      
      // Convert the template HTML to a filled document for preview
      // Use the stored placeholder values to fill in the content
      const placeholderValues = (document.placeholder_values as Record<string, string>) || {};
      const filledContent = prepareDocumentForPreview(rawContent, placeholderValues);
      
      console.log('Document content processed for preview');
      
      setPreviewContent(filledContent);
      setPreviewDocument(document);
    } catch (error) {
      console.error('Error previewing document:', error);
      toast({
        title: "Error",
        description: "Failed to preview document",
        variant: "destructive",
      });
    }
  };

  const useDocumentVersions = (documentId: string | null) => {
    return useQuery({
      queryKey: ['document-versions', documentId],
      queryFn: async () => {
        if (!documentId) return [];
        
        console.log('Fetching versions for document:', documentId);
        
        // First get the current document as the latest version
        const { data: currentDocument, error: currentDocError } = await (supabase as any)
          .from('documents')
          .select('*')
          .eq('id', documentId)
          .single();
          
        if (currentDocError) {
          console.error('Error fetching current document:', currentDocError);
          throw currentDocError;
        }
        
        // Then get historical versions from document_versions table
        const { data: historicalVersions, error } = await (supabase as any)
          .from('document_versions')
          .select('*')
          .eq('document_id', documentId)
          .order('created_at', { ascending: false });
          
        if (error) {
          console.error('Error fetching document versions:', error);
          throw error;
        }
        
        // Create a "current version" entry from the document itself
        // Fix the type issue by explicitly casting or transforming the placeholder_values
        const placeholderValues = currentDocument.placeholder_values as Record<string, string> | null;
        
        const currentVersion: DocumentVersion = {
          id: `current-${currentDocument.id}`,
          document_id: currentDocument.id,
          version_number: currentDocument.version_number,
          content: currentDocument.content,
          placeholder_values: placeholderValues,
          created_at: currentDocument.updated_at || currentDocument.created_at,
          created_by: currentDocument.user_id
        };
        
        // Combine current version with historical versions
        const allVersions = [currentVersion, ...historicalVersions];
        
        // Make sure all versions have correctly typed placeholder_values
        const typedVersions = allVersions.map(version => ({
          ...version,
          placeholder_values: version.placeholder_values as Record<string, string> | null
        }));
        
        console.log('Fetched versions:', typedVersions);
        return typedVersions as DocumentVersion[];
      },
      enabled: !!documentId,
      staleTime: 0, // Always fetch fresh data
      refetchOnWindowFocus: true // Refetch when the window regains focus
    });
  };

  const fetchDocumentVersion = async (documentId: string, versionNumber: number): Promise<DocumentVersion | null> => {
    console.log('Fetching specific version:', versionNumber, 'for document:', documentId);
    
    try {
      // First check if it's the current version
      const { data: currentDocument, error: currentDocError } = await (supabase as any)
        .from('documents')
        .select('*')
        .eq('id', documentId)
        .single();
        
      if (currentDocError) {
        console.error('Error fetching current document:', currentDocError);
        throw currentDocError;
      }
      
      // If it's the current version, return it
      if (currentDocument.version_number === versionNumber) {
        const placeholderValues = currentDocument.placeholder_values as Record<string, string> | null;
        return {
          id: `current-${currentDocument.id}`,
          document_id: currentDocument.id,
          version_number: currentDocument.version_number,
          content: currentDocument.content,
          placeholder_values: placeholderValues,
          created_at: currentDocument.updated_at || currentDocument.created_at,
          created_by: currentDocument.user_id
        };
      }
      
      // Otherwise, fetch from document_versions table
      const { data: version, error } = await (supabase as any)
        .from('document_versions')
        .select('*')
        .eq('document_id', documentId)
        .eq('version_number', versionNumber)
        .single();
        
      if (error) {
        console.error('Error fetching document version:', error);
        throw error;
      }
      
      if (!version) {
        console.log('Version not found:', versionNumber);
        return null;
      }
      
      // Ensure placeholder_values is properly typed
      const typedVersion = {
        ...version,
        placeholder_values: version.placeholder_values as Record<string, string> | null
      };
      
      console.log('Fetched version:', typedVersion);
      return typedVersion as DocumentVersion;
    } catch (error) {
      console.error('Error in fetchDocumentVersion:', error);
      throw error;
    }
  };

  return {
    previewDocument,
    setPreviewDocument,
    previewContent,
    setPreviewContent,
    downloadDocument,
    setDownloadDocument,
    editingDocument,
    setEditingDocument,
    handlePreview,
    deleteMutation,
    updateDocumentMutation,
    useDocumentVersions,
    fetchDocumentVersion
  };
}
